export interface ImageSize {
  width: number;
  height: number;
}

/**
 * The size a PNG, GIF, WebP or JPEG says it is, from its header, before anything decodes it (a
 * GIF's, from its frames' too). Null when the header does not say (a truncated or odd file), and
 * then it is not shown.
 */
export function declaredImageSize(bytes: Uint8Array): ImageSize | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const has = (end: number) => end <= bytes.length;
  const ascii = (offset: number, text: string) =>
    has(offset + text.length) &&
    [...text].every((char, index) => bytes[offset + index] === char.charCodeAt(0));

  // PNG: IHDR is always the first chunk.
  if (bytes[0] === 0x89 && ascii(1, "PNG") && ascii(12, "IHDR") && has(24)) {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (ascii(0, "GIF8") && has(10)) return gifSize(bytes, view);
  if (ascii(0, "RIFF") && ascii(8, "WEBP")) {
    // Lossy: a key frame's start code, then 14-bit sizes.
    if (ascii(12, "VP8 ") && has(30) && bytes[23] === 0x9d && bytes[24] === 0x01) {
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }
    // Lossless: a signature byte, then sizes less one in 14 bits each.
    if (ascii(12, "VP8L") && has(25) && bytes[20] === 0x2f) {
      const bits = view.getUint32(21, true);
      return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
    }
    // Extended: the canvas, sizes less one in 24 bits each.
    if (ascii(12, "VP8X") && has(30)) {
      const uint24 = (offset: number) => view.getUint16(offset, true) + (bytes[offset + 2] << 16);
      return { width: uint24(24) + 1, height: uint24(27) + 1 };
    }
    return null;
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return jpegSize(bytes, view);
  return null;
}

/**
 * A GIF's logical screen, grown to hold every frame its descriptors place: a browser decodes a
 * frame at its own size, whatever the screen says. The walk stops at the trailer, at a block it
 * does not know, or at the end of the bytes; each step moves on, so it ends within them.
 */
function gifSize(bytes: Uint8Array, view: DataView): ImageSize {
  let width = view.getUint16(6, true);
  let height = view.getUint16(8, true);
  // A colour table's bytes, from the flags that say it is there and its size.
  const table = (flags: number) => (flags & 0x80 ? 3 << ((flags & 0x07) + 1) : 0);
  let at = 13 + table(bytes[10] ?? 0);
  // Data sub-blocks, each its length then its bytes, until one of length 0.
  const skipSubBlocks = () => {
    while (at < bytes.length && bytes[at] !== 0) at += bytes[at] + 1;
    at++;
  };
  while (at < bytes.length) {
    const block = bytes[at];
    if (block === 0x21) {
      // An extension: its label, then its sub-blocks.
      at += 2;
      skipSubBlocks();
    } else if (block === 0x2c && at + 10 <= bytes.length) {
      // An image descriptor: left, top, width, height, then flags.
      width = Math.max(width, view.getUint16(at + 1, true) + view.getUint16(at + 5, true));
      height = Math.max(height, view.getUint16(at + 3, true) + view.getUint16(at + 7, true));
      // Past its colour table and its LZW code size, to its image data.
      at += 10 + table(bytes[at + 9]) + 1;
      skipSubBlocks();
    } else break;
  }
  return { width, height };
}

/** A JPEG's size from its frame header, walking the segments that come before it. */
function jpegSize(bytes: Uint8Array, view: DataView): ImageSize | null {
  let at = 2;
  while (at + 4 <= bytes.length) {
    // Junk between segments: decoders skip to the next marker (libjpeg does), so this does too.
    if (bytes[at] !== 0xff) {
      at = bytes.indexOf(0xff, at);
      if (at < 0) return null;
      continue;
    }
    const marker = bytes[at + 1];
    // Fill bytes, and markers that stand alone.
    if (marker === 0xff) {
      at++;
      continue;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2;
      continue;
    }
    // FF 00 is a stuffed byte, not a marker: libjpeg drops it and looks on for the next one.
    if (marker === 0x00) {
      at += 2;
      continue;
    }
    // Reserved markers: libjpeg stops on them, so nothing would be shown.
    if (marker >= 0x02 && marker <= 0xbf) return null;
    // Start of scan or end of image: no frame header came first.
    if (marker === 0xda || marker === 0xd9) return null;
    // SOF0 to SOF15, less DHT (C4), JPG (C8) and DAC (CC), which share the range.
    const frame = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
    if (frame) {
      if (at + 9 > bytes.length) return null;
      return { width: view.getUint16(at + 7), height: view.getUint16(at + 5) };
    }
    at += 2 + view.getUint16(at + 2);
  }
  return null;
}
